<template>
  <div>
    <div v-if="!editOpened" class="flex flex-col">
      <div
        class="py-4 px-6 flex items-center justify-between border-b border-slate-400"
      >
        <BlTextSubtitle2 text="Report details" class="text-slate-700" />
        <div class="flex items-center gap-2">
          <BlInputButton
            label="Edit"
            type="outlined"
            dense
            @clicked="editOpened = true"
          />
          <BlIcon
            icon="close"
            class="h-6 w-6"
            clickable
            @clicked="$emit('close')"
          />
        </div>
      </div>
      <div class="py-4 px-6 flex flex-col gap-3">
        <div class="flex flex-col gap-1">
          <BlTextBody3 text="Report name" />
          <BlTextSubtitle3 :text="report.name" />
        </div>
        <div class="h-0.5 w-full flex-shrink-0 bg-slate-200 rounded"></div>
        <div class="flex flex-col gap-1">
          <BlTextBody3 text="Date range" />
          <div>
            <BlTextSubtitle3 :text="displayDate({ date: report.start_date })" />
            <BlTextSubtitle3 text=" - " />
            <BlTextSubtitle3 :text="displayDate({ date: report.end_date })" />
          </div>
        </div>
        <div class="h-0.5 w-full flex-shrink-0 bg-slate-200 rounded"></div>
        <div class="flex flex-col gap-1">
          <BlTextBody3 text="Include header in report" />
          <BlTextSubtitle3 :text="report.include_header ? 'Yes' : 'No'" />
        </div>
      </div>
    </div>
    <div v-if="editOpened" class="flex flex-col">
      <div
        class="py-4 px-6 flex items-center justify-between border-b border-slate-400"
      >
        <BlTextSubtitle2 text="Edit report details" class="text-slate-700" />
      </div>
      <div class="py-4 px-6 flex flex-col gap-3">
        <div class="flex flex-col gap-1">
          <BlTextSubtitle3 text="Report name" />
          <BlTextBody2
            text="You can change the report name here, and this will be reflected as the title in the report."
          />
          <BlInputText :input="nameInput" @changed="inputHandler" />
        </div>
        <div class="h-0.5 w-full flex-shrink-0 bg-slate-200 rounded"></div>
        <div class="flex flex-col gap-1">
          <BlTextSubtitle3 text="Date range" />
          <BlTextBody2
            text="Adjust the date range here to apply it to any new graphs you add. This will not change the date range of existing graphs already included in the report."
          />
          <div class="flex items-center gap-4">
            <BlInputDate :input="startDateInput" @changed="inputHandler" />
            <BlInputDate :input="endDateInput" @changed="inputHandler" />
          </div>
        </div>
        <div class="h-0.5 w-full flex-shrink-0 bg-slate-200 rounded"></div>
        <div class="flex flex-col gap-1">
          <div class="flex items-center justify-between">
            <BlTextSubtitle3 text="Include header in report" />
            <BlInputToggle
              :checked="includeHeaderInput"
              @changed="includeHeaderInput = !includeHeaderInput"
            />
          </div>
          <BlTextBody2
            text="The header is included by default when the report is generated as a PDF. You can switch off the toggle to remove it from the PDF."
          />
          <div
            v-if="currentUser.role.includes('admin')"
            class="py-2 flex items-center justify-center gap-1 rounded bg-cornflower-200"
          >
            <BlTextBody2
              text="To adjust the logo or center name, visit"
              class="text-cornflower-800 flex-wrap"
            />
            <router-link
              to="/center"
              target="_blank"
              class="flex items-center gap-1"
            >
              <BlTextBody2
                text="Center Settings!"
                class="text-cornflower-800 flex-wrap font-bold underline"
              />
              <BlIcon icon="square-up" color="cornflower-8" class="h-4 w-4" />
            </router-link>
          </div>
          <div v-else class="py-2 px-2 rounded bg-cornflower-200">
            <BlTextBody2
              text="To adjust the logo or center name, ask an Admin to change it in "
              class="text-cornflower-800 flex-wrap"
            />
            <BlTextBody2
              text="Center Settings."
              class="text-cornflower-800 flex-wrap font-bold"
            />
          </div>
        </div>
      </div>
      <div class="py-4 px-6 grid grid-cols-2 items-center gap-4">
        <BlInputButton
          label="Cancel"
          type="plain"
          @clicked="editOpened = false"
        />
        <BlInputButton
          label="Update"
          :loading="updateLoading"
          @clicked="onUpdate"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import moment from "moment";
import { displayDate } from "@/plugins/helper.js";

import BlTextSubtitle2 from "../../BlTextSubtitle2.vue";
import BlTextSubtitle3 from "../../BlTextSubtitle3.vue";
import BlTextBody2 from "../../BlTextBody2.vue";
import BlTextBody3 from "../../BlTextBody3.vue";
import BlInputText from "../../BlInputText.vue";
import BlInputDate from "../../BlInputDate.vue";
import BlInputToggle from "../../BlInputToggle.vue";
import BlInputButton from "../../BlInputButton.vue";
import BlIcon from "../../BlIcon.vue";

export default {
  name: "ReportSetting",
  components: {
    BlTextSubtitle2,
    BlTextSubtitle3,
    BlTextBody2,
    BlTextBody3,
    BlInputText,
    BlInputDate,
    BlInputToggle,
    BlInputButton,
    BlIcon
  },
  props: {
    // @close
  },
  data() {
    return {
      editOpened: false,
      updateLoading: false,
      nameInput: {
        placeholder: "Type report name",
        model: ""
      },
      startDateInput: { model: "" },
      endDateInput: { model: "" },
      includeHeaderInput: true
    };
  },
  computed: {
    ...mapGetters(["currentUser", "report"])
  },
  watch: {
    editOpened(val) {
      if (val) {
        this.nameInput.model = this.report.name;
        this.startDateInput.model = moment(this.report.start_date).format(
          "YYYY-MM-DD"
        );
        this.endDateInput.model = moment(this.report.end_date).format(
          "YYYY-MM-DD"
        );
        this.includeHeaderInput = this.report.include_header;
      }
    }
  },
  mounted() {},
  methods: {
    ...mapActions(["updateReport"]),
    displayDate,
    inputHandler(payload) {
      payload.input.model = payload.value;
    },
    async onUpdate() {
      const data = {
        slug: this.report.slug,
        report: {
          name: this.nameInput.model,
          start_date: this.startDateInput.model,
          end_date: this.endDateInput.model,
          include_header: this.includeHeaderInput
        }
      };
      this.updateLoading = true;
      const { success } = await this.updateReport(data);
      this.updateLoading = false;
      if (!success) return;
      this.editOpened = false;
    }
  }
};
</script>



// WEBPACK FOOTER //
// src/components/reports/helper/Setting.vue